import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteIcon from "@mui/icons-material/Delete";

const DEBUG = process.env.REACT_APP_DEBUG === "yes";
const debugLog = (...args) => {
  if (DEBUG) console.log(...args);
};

const getScholarshipDisplayName = (scholarship) => {
  const line1 = scholarship?.name || scholarship?.scholarship_name || "";
  const line2 =
    scholarship?.name_line_2 || scholarship?.scholarship_name_line_2 || "";

  return line2 ? `${line1} - ${line2}` : line1;
};

const ScholarshipDetailCard = ({
  selectedScholarship,
  isSchoolAdmin,
  isRecorder,
  canDriveSelection,
  setAffiliationModalOpen,
  setSelectedAwardIdToUnassign,
  setConfirmationOpen,
}) => {
  const pluralizeAwardType = (type, count) => {
    if (type === "Bursary") {
      return count > 1 ? "Bursaries" : "Bursary";
    }
    return count > 1 ? `${type}s` : type;
  };
  debugLog("selectedScholarship", selectedScholarship);

  return (
    <Paper
      sx={{ padding: 3, mt: 3, backgroundColor: "#e0f7fa", borderRadius: 2 }}
    >
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {selectedScholarship.awardHistory?.length >=
        selectedScholarship.number_of_awards ? (
          <CheckIcon sx={{ verticalAlign: "middle", color: "green", mr: 1 }} />
        ) : selectedScholarship.awardHistory?.length > 0 ? (
          <WarningAmberIcon
            sx={{ verticalAlign: "middle", color: "orange", mr: 1 }}
          />
        ) : null}
        {getScholarshipDisplayName(selectedScholarship)} -{" "}
        {selectedScholarship.number_of_awards}{" "}
        {pluralizeAwardType(
          selectedScholarship.award_type,
          selectedScholarship.number_of_awards
        )}{" "}
        @ ${selectedScholarship.award_amount}
      </Typography>

      {selectedScholarship.description && (
        <Typography variant="body2" gutterBottom>
          <strong>Description:</strong> {selectedScholarship.description}
        </Typography>
      )}

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={6}>
          {["priority1", "priority2", "priority3"].map((key, idx) => {
            const label = `Priority ${idx + 1}`;
            const value = selectedScholarship[key];
            return (
              value && (
                <Typography variant="body2" key={key}>
                  <strong>{label}:</strong> {value}
                  {value === "Affiliation" && (
                    <Link
                      component="button"
                      variant="body2"
                       onClick={() => {
                        setAffiliationModalOpen(true);
                      }}
                      sx={{ ml: 1 }}
                    >
                      (View Students)
                    </Link>
                  )}
                </Typography>
              )
            );
          })}
          {selectedScholarship.specific_subject && (
            <Typography variant="body2">
              <strong>Specific Subject:</strong>{" "}
              {selectedScholarship.specific_subject}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          {selectedScholarship.gender_specific && (
            <Typography variant="body2">
              <strong>Gender-Specific:</strong>{" "}
              {selectedScholarship.gender_specific}
            </Typography>
          )}
          {selectedScholarship.field_of_study && (
            <Typography variant="body2">
              <strong>Field of Study:</strong>{" "}
              {selectedScholarship.field_of_study}
            </Typography>
          )}
          {selectedScholarship.subject_area && (
            <Typography variant="body2">
              <strong>Subject Area:</strong> {selectedScholarship.subject_area}
            </Typography>
          )}
          {selectedScholarship.claim_period && (
            <Typography variant="body2">
              <strong>Claim Period:</strong> {selectedScholarship.claim_period}{" "}
              {selectedScholarship.claim_period > 1 ? "years" : "year"}
            </Typography>
          )}
        </Grid>
      </Grid>

      {selectedScholarship.awardHistory?.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Award History ({selectedScholarship.awardHistory.length}):
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "1px solid #ccc", borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>School</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Year</TableCell>
	                  {canDriveSelection && (
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      Delete
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedScholarship.awardHistory.map((award) => (
                  <TableRow key={award.award_id}>
                    <TableCell>
                      {award.first_name} {award.last_name}
                    </TableCell>
                    <TableCell>
                      {award.school}
                    </TableCell>
                    <TableCell>
                      ${award.award_amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{award.award_year}</TableCell>
	                    {canDriveSelection && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedAwardIdToUnassign(award.award_id);
                            setConfirmationOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  );
};

export default ScholarshipDetailCard;
